import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { NavigationInterface } from 'app/layout/components/navigation/models/navigation-interface';
import { InfoUserCustomerProjection } from 'app/shared/prothesis-api-projections/info-user-customer.projection';
import { Observable, ReplaySubject } from 'rxjs';
import { AuthService } from '../auth.service';
import { ConfigurationService } from '../configuration.service';
import { AreaMenu } from '../left-menu/area-menu.enum';
import { LeftMenuService } from '../left-menu/left-menu.service';
import { DatadogContextStateService } from '../datadog-context-state.service';
import { NotifyService } from '../notify/notify.service';
import { WorkingLaboratoryService } from '../prothesis-api/working-laboratory.service';
import { SessionStorageService } from '../session-storage.service';
import { WorkingTenantServiceInterface } from '../working-tenant-service.interface';

@Injectable({
  providedIn: 'root',
})
export class WorkingCustomerService implements WorkingTenantServiceInterface {
  public authService: AuthService;
  public exploredUuid$: Observable<string>;
  public tenantBasePath = '/Customer';

  private _configurationService = inject(ConfigurationService);
  private _contextStateService: DatadogContextStateService = inject(DatadogContextStateService);
  private _customerCode: string | null;
  private _customerGroupFlag: boolean | null;
  private _customerGroupName: string | null;
  private _customerMemberFlag: boolean | null;
  private _customerName: string | null;
  private _exploredUuidSubject = new ReplaySubject<string>();
  private _exploringFlag: boolean | null = false;
  private _fuseNavigationService = inject(FuseNavigationService);
  private _httpClient = inject(HttpClient);
  private _isAdminOrSuperAdminFlag: boolean;
  private _leftMenuService = inject(LeftMenuService);
  private _notifyService = inject(NotifyService);
  private _sessionStorageService = inject(SessionStorageService);
  private _tenantUuid: string | null;
  private _workingLaboratoryService = inject(WorkingLaboratoryService);

  public constructor() {
    this._leftMenuService.workingCustomerService = this;
    this.exploredUuid$ = this._exploredUuidSubject.asObservable();
  }

  public get customerCode(): string | null {
    return this._customerCode;
  }

  public set customerCode(value: string | null) {
    const oldCustomerCode = this._customerCode;
    this._customerCode = value;

    if (this._customerCode !== oldCustomerCode) {
      if (this._customerCode) {
        this._sessionStorageService.write('working_customer_code', this._customerCode);
      } else {
        this._sessionStorageService.removeItem('working_customer_code');
      }
      this._contextStateService.setCustomerCode(this._customerCode);
    }
  }

  public get customerGroupFlag(): boolean | null {
    return this._customerGroupFlag;
  }

  public set customerGroupFlag(value: boolean | null) {
    const oldCustomerGroupFlag = this._customerGroupFlag;
    this._customerGroupFlag = value;

    if (this._customerGroupFlag !== oldCustomerGroupFlag) {
      if (this._customerGroupFlag !== undefined && this._customerGroupFlag !== null) {
        this._sessionStorageService.write('working_customer_group_flag', this._customerGroupFlag);
      } else {
        this._sessionStorageService.removeItem('working_customer_group_flag');
      }
    }
  }

  public get customerGroupName(): string | null {
    return this._customerGroupName;
  }

  public set customerGroupName(value: string | null) {
    const oldCustomerGroupName = this._customerGroupName;
    this._customerGroupName = value;

    if (this._customerGroupName !== oldCustomerGroupName) {
      if (this._customerGroupName) {
        this._sessionStorageService.write('working_customer_group_name', this._customerGroupName);
      } else {
        this._sessionStorageService.removeItem('working_customer_group_name');
      }
    }
  }

  public get customerMemberFlag(): boolean | null {
    return this._customerMemberFlag;
  }

  public set customerMemberFlag(value: boolean | null) {
    const oldCustomerMemberFlag = this._customerMemberFlag;
    this._customerMemberFlag = value;

    if (this._customerMemberFlag !== oldCustomerMemberFlag) {
      if (this._customerMemberFlag !== undefined && this._customerMemberFlag !== null) {
        this._sessionStorageService.write('working_customer_member_flag', this._customerMemberFlag);
      } else {
        this._sessionStorageService.removeItem('working_customer_member_flag');
      }
    }
  }

  public get customerName(): string | null {
    return this._customerName;
  }

  public set customerName(value: string | null) {
    const oldCustomerName = this._customerName;
    this._customerName = value;

    if (this._customerName !== oldCustomerName) {
      if (this._customerName) {
        this._sessionStorageService.write('working_customer_name', this._customerName);
      } else {
        this._sessionStorageService.removeItem('working_customer_name');
      }
    }
  }

  public get exploringFlag(): boolean | null {
    return this._exploringFlag ?? false;
  }

  public set exploringFlag(value: boolean | null) {
    const oldExploringFlag = this._exploringFlag;
    this._exploringFlag = value;

    if (this._exploringFlag !== oldExploringFlag) {
      if (this._exploringFlag !== undefined && this._exploringFlag !== null) {
        this._sessionStorageService.write('working_customer_exploringFlag', this._exploringFlag);
      } else {
        this._sessionStorageService.removeItem('working_customer_exploringFlag');
      }
    }
  }

  public get isAdminOrSuperAdminFlag(): boolean {
    return this._isAdminOrSuperAdminFlag;
  }

  public set isAdminOrSuperAdminFlag(value: boolean) {
    this._isAdminOrSuperAdminFlag = value;
  }

  public get tenantUuid(): string | null {
    return this._tenantUuid;
  }

  public set tenantUuid(value: string | null) {
    const oldTenantUuid = this._tenantUuid;
    this._tenantUuid = value;

    if (this._tenantUuid !== oldTenantUuid) {
      if (this._tenantUuid) {
        this._sessionStorageService.write('working_customer_uuid', this._tenantUuid);
      } else {
        this._sessionStorageService.removeItem('working_customer_uuid');
      }
    }
  }

  public clear(): void {
    this.customerCode = null;
    this.customerGroupFlag = null;
    this.customerGroupName = null;
    this.customerMemberFlag = null;
    this.customerName = null;
    this.exploringFlag = null;
    this.tenantUuid = null;
  }

  public explore(customerUuid: string | null | undefined, notifyFlag = true): Observable<InfoUserCustomerProjection> {
    this.exploringFlag = true;

    return new Observable<InfoUserCustomerProjection>((observer) => {
      this.getInfo(customerUuid).subscribe(
        (infoUserCustomerProjection: InfoUserCustomerProjection) => {
          this.customerCode = infoUserCustomerProjection.customer.code;
          this.customerGroupFlag = infoUserCustomerProjection.customer.customerGroupFlag;
          this.customerGroupName = infoUserCustomerProjection.customer.customerGroupName;
          this.customerMemberFlag = infoUserCustomerProjection.customer.customerMemberFlag;
          this.customerName = infoUserCustomerProjection.customer.name;
          this.tenantUuid = infoUserCustomerProjection.customer.uuid;
          this.exploreInternal(notifyFlag);
          observer.next(infoUserCustomerProjection);
          observer.complete();
        },
        () => {
          observer.complete();
        },
      );
    });
  }

  public getInfo(customerUuid: string | null | undefined): Observable<InfoUserCustomerProjection> {
    const actionUrl = `${this._configurationService.ProthesisCustomerApiAddress}/Customer/${customerUuid}/Customer/GetInfo`;
    return this._httpClient.get<InfoUserCustomerProjection>(actionUrl);
  }

  public stopExploring(): void {
    const customerName = this._customerName;
    const customerCode = this._customerCode;
    this.clear();

    // Set navigation menu
    const areaMenus = [AreaMenu.Laboratory];
    if (this.isAdminOrSuperAdminFlag) {
      areaMenus.unshift(AreaMenu.Admin);
    }
    const navigation = this._leftMenuService.getNavigation(
      areaMenus,
      this._workingLaboratoryService.laboratoryDemoModeFlag,
      this.isAdminOrSuperAdminFlag,
      this._workingLaboratoryService.laboratoryName,
    );
    if (this.isAdminOrSuperAdminFlag) {
      this.buildAdminMenu(navigation);
    }

    this._fuseNavigationService.unregister('main');
    this._fuseNavigationService.register('main', navigation);
    if (customerCode && customerName) {
      this._notifyService.displayInformationMessagesNow(
        `Espace client ${customerCode} - ${customerName} n'est plus en cours d'exploration.`,
      );
    }

    this._exploredUuidSubject.next(null!);
  }

  public tryExploring(): void {
    this.customerCode = this._sessionStorageService.read('working_customer_code')!;
    this._customerGroupFlag = this._sessionStorageService.read('working_customer_group_flag');
    this._customerGroupName = this._sessionStorageService.read('working_customer_group_name');
    this._customerMemberFlag = this._sessionStorageService.read('working_customer_member_flag');
    this._customerName = this._sessionStorageService.read('working_customer_name');
    this._exploringFlag = this._sessionStorageService.read('working_customer_exploringFlag');
    this._tenantUuid = this._sessionStorageService.read('working_customer_uuid');

    if (
      this._customerCode &&
      this._exploringFlag !== null &&
      this._exploringFlag !== undefined &&
      this._customerGroupFlag !== null &&
      this._customerGroupFlag !== undefined &&
      this._customerMemberFlag !== null &&
      this._customerMemberFlag !== undefined &&
      this._customerName &&
      this._tenantUuid
    ) {
      this.exploreInternal(false);
    }
  }

  private buildAdminMenu(navigations: NavigationInterface[]): void {
    const laboratoryName = this._workingLaboratoryService.laboratoryName;
    const laboratoryCode = this._workingLaboratoryService.laboratoryCode;
    const laboratoryNavigationItem: NavigationInterface | undefined = navigations.find(
      (fuseNavigationItem: NavigationInterface) => fuseNavigationItem.id === 'laboratory-area',
    );
    laboratoryNavigationItem!.translate = undefined;
    laboratoryNavigationItem!.text = `${laboratoryCode} - ${laboratoryName}`;
  }

  private exploreInternal(notifyFlag: boolean): void {
    // Set navigation menu
    const areaMenus = [AreaMenu.Laboratory];
    if (this.isAdminOrSuperAdminFlag) {
      areaMenus.unshift(AreaMenu.Admin);
    }

    if (this.customerMemberFlag || !this._workingLaboratoryService.laboratoryRateAutoPublishFlag) {
      areaMenus.push(AreaMenu.CustomerMember);
    } else {
      areaMenus.push(AreaMenu.CustomerAndCustomerGroup);
    }

    const navigation: NavigationInterface[] = this._leftMenuService.getNavigation(
      areaMenus,
      this._workingLaboratoryService.laboratoryDemoModeFlag,
      this.isAdminOrSuperAdminFlag,
      this._workingLaboratoryService.laboratoryName,
    );
    const navigationItem: NavigationInterface | undefined = navigation.find(
      (fuseNavigationItem: NavigationInterface) => fuseNavigationItem.id === 'customer-area',
    );
    navigationItem!.translate = undefined;
    navigationItem!.text = `${this.customerCode} - ${this._customerName}`;

    if (this.isAdminOrSuperAdminFlag) {
      this.buildAdminMenu(navigation);
    }

    this._fuseNavigationService.unregister('main');
    this._fuseNavigationService.register('main', navigation);

    if (notifyFlag) {
      this._notifyService.displayInformationMessagesNow(
        `Espace client ${this._customerCode} - ${this._customerName} en cours d'exploration.`,
      );
    }

    this._exploredUuidSubject.next(this.tenantUuid!);
  }
}
